/**
 * 用户中心错误码（C 端全集）。
 *
 * 来源：接口 4.0 + 后端 2026-09-03 的逐条答复（`` #8）。
 * 后端已确认前端此前自制的那张表**全部正确、无一多余**，并补上了下面标注
 * 「4.0 补」的 12 个码 —— 加上它们才是全集。
 *
 * ⚠️ CRM 那张表（40304 菜单权限、40901~40903 职责分离、42801/42802 确认码与设密链接）
 * 在 C 端**永远不会出现**，不要照着它写分支。
 * 接口 4.0 的 `info.description` 里仍然是 CRM 那份、`CustErrors` schema 也仍未定义
 * 以本文件为准。
 *
 * 2026-10-10 对后端源码（`CustErrors.java` / `FundErrors.java`）逐码核对：
 * 删掉后端不存在的 40001 / 40102 / 42301，补上后端会发而此前没登记的 42225 / 40921。
 */
export const CODE = {
  /**
   * 裸 401「未登录或登录已过期」。
   *
   * 2026-09-07 之前网关 AuthFilter 与服务侧 AuthGuardInterceptor 在 token 缺失 / 验签失败时返回的就是它，
   * 而这里只认 40101，于是本地 token 没了（另一标签页退出登录）之后页面停在原地只显示一行红字、不回登录页
   * （改密页复现）。后端同日已改为 40101，这里仍保留 401 的兼容：联调环境滞后一版也不会再卡住。
   * ⚠️ 登录接口的 401 是「账号或密码错误」，不走踢出逻辑 —— 见 http.ts 的 isAuthEndpoint。
   */
  UNAUTHENTICATED: 401,
  /** 登录已过期 / 未登录（网关与服务拦截器 2026-09-07 起同码）→ 跳登录页 */
  UNAUTHORIZED: 40101,
  /** 4.0 补：会话已吊销（改密 / 账号禁用 / 互踢——被顶下线也走这一码，后端没有单独的 40102），须重新登录 */
  SESSION_REVOKED: 40111,

  /** 必须先修改密码（密码已过期，R-18 接口层同步拦截） */
  MUST_CHANGE_PWD: 40301,

  /** portal 闸：当前 portal_mode / 进度不允许该能力（R-10） */
  PORTAL_GATE: 40311,
  /** 待提交状态访问激活任务被拦截（R-17） */
  ACTIVATION_LOCKED: 40312,
  /**
   * 登录已停用（账户禁用 / 关闭联动，不披露细节）。
   * 会话中途被停用时 `CustomerSessionGuard` 会吊销会话并发它 —— 所以它走**踢出**路径（清 token 回登录页），不是只 toast。
   */
  LOGIN_DISABLED: 40313,
  /** 资格 / 黑名单 / 查重命中：统一婉拒，不披露原因 */
  DECLINED: 40314,

  /**
   * 当前进度 / 状态不允许该动作（如非 SUIT_FAILED_1 却调重新申请）。
   * 7.0 起 MT5 交易密码重置也用它：发链接时账户状态不允许；落地页提交时 **MT5 明确拒绝该密码（链接放回，可再试）**。
   */
  STATE_CONFLICT: 40911,

  /** 密码连续错误过多（5 次）账号锁定 / 锁定中（30 分钟）：后端只有这一个码（`CustErrors.ACCOUNT_LOCKED`），没有 42301 */
  LOGIN_LOCKED: 42311,

  /** 验证码错误 / 已过期，或频控 */
  CODE_INVALID: 42211,
  /** 冷却期未满（message 含到期提示） */
  COOLING: 42212,

  /** 链接无效或已过期（重置登录密码链接、MT5 交易密码重置链接、续填令牌） */
  LINK_EXPIRED: 42811,

  /** 邮件通道不可用 */
  MAIL_DOWN: 50201,
  /**
   * 依赖的外部通道不可用：Sumsub 凭据未配置 / MT5 bridge 不可达且无缓存。
   * 7.0 起 MT5 交易密码重置落地页也用它：**结果未确认、链接不放回** —— 密码可能已改，页面停住、不给重试（同 50321 的处理哲学）。
   */
  CHANNEL_DOWN: 50311,

  /** 表单字段校验未通过，message 形如「字段名: 原因」 */
  VALIDATION: 422,
  /** 4.0 补：已有一张银行账号在审核中，不可再提交 */
  CONFLICT: 409,
  /** 4.0 补：单 IP 频控（验证码 5/min、登录 10/min…），网关层返回 */
  RATE_LIMITED: 429,

  /* ---------- 文件（接口 2.0 起） ---------- */
  /** 413 文件超过 10MB */
  FILE_TOO_LARGE: 41311,
  /** 422 文件类型不支持（服务端按内容魔数识别，后缀不作数） */
  FILE_TYPE: 42213,
  /** 422 该类别不允许 C 端上传（WS_DOC / WITHDRAWAL_VOUCHER / OPEN_FORM 仅 CRM 端） */
  FILE_CATEGORY: 42214,
  /** 404 文件不存在 / 非本人 / 已删除 */
  FILE_NOT_FOUND: 40411,
  /** 503 文件存储不可用 */
  FILE_STORE_DOWN: 50312,
  /**
   * 4.0 补：422 绑卡对账单 ref 无效（非本人 / 类别不是 CM_DOC / 已挂到别的单据）。
   *
   * 4.0 起后端**真的会校验并挂接** `statementRef` 了 —— 2.0 时它只校验非空，
   * 于是上传满 24h 的文件会被回收器清掉、绑卡却仍然"提交成功"。 与本码。
   */
  FILE_REF_INVALID: 42215,

  /** 422 导出结果超过上限（报表导出 5,000 行），请缩小时间范围。与 CRM 列表导出同码；文案后端给，走全局 toast */
  EXPORT_TOO_LARGE: 42201,

  /* ---------- 4.0 新增：资金组 `/api/c/funding/**` ---------- */
  /**
   * 403 该账户当前方向被禁（冻结 / 禁用账户）。
   *
   * ⭐ **前端不要写死方向** —— 冻结账户到底能不能入金仍待风控与合规裁决，
   * 后端已做成配置（`cfd.funding.frozen-deposit-blocked` / `frozen-withdraw-blocked`），
   * 缺省是 CRM 口径「禁入金、允出金」。前端一律「可提交、按码提示」，
   * 拍板后后端改一行配置、前端零改动。
   */
  FUNDING_DIRECTION_BLOCKED: 40321,
  /**
   * 404 目标不存在（`FundErrors.NOT_FOUND`）：C 端提交时最常见的是 **MT5 账号非本人 / 模拟户**；
   * 银行账户不存在 / 非本人 / 未审核通过也是它。message 由后端说清是哪个。
   */
  BANK_ACCOUNT_INVALID: 40421,
  /** 409 资金状态机拒绝（`FundErrors.STATE_CONFLICT`，如提案状态已变化）；C 端直接展示 message */
  FUNDING_STATE_CONFLICT: 40921,
  /** 422 金额非法（≤0、超过可提、精度不对） */
  AMOUNT_INVALID: 42221,
  /** 422 该方向暂无生效汇率（跨币种提交才会遇到） */
  NO_RATE: 42222,
  /** 422 汇款水单 ref 无效（非本人 / 类别不是 DEPOSIT_VOUCHER / 已挂他单） */
  VOUCHER_INVALID: 42224,
  /**
   * 422 所选收款账户不在后端配置集合内（`FundErrors.RECEIVING_ACCOUNT_INVALID`）：运营改了 Nacos 配置而页面还是旧列表。
   * DepositView 收到它要清掉所选并失效 `receiving-accounts` 缓存，让客户重选。
   */
  RECEIVING_ACCOUNT_INVALID: 42225,
  /**
   * 503 ⚠️ 出金专用：**MT5 扣款结果未知，未建单**。
   *
   * 前端必须提示「请稍后在申请记录中确认」，并且**禁止立即重试** ——
   * 系统会自动收敛（已扣则补建单、未扣则关闭）。重复提交可能导致重复扣款。
   */
  WITHDRAW_UNKNOWN: 50321,
  /** 503 资金下游不可用（MT5 bridge / 提案服务） */
  FUNDING_DOWN: 50322,
} as const

/**
 * 这些码**不弹全局 toast**，由页面自己就地呈现。
 *
 * ⚠️ 往这里加码之前先确认页面真的接住了它 —— 加进来却没人处理
 * 就是一次静默失败（"没接线"那一类，类型检查与构建都发现不了）。
 * 下面每一条都注明了由谁处理。
 */
export const SILENT_CODES: number[] = [
  CODE.CHANNEL_DOWN, // 身份认证降级页 / 资金卡「暂时取不到」/ MT5 密码重置落地页「结果未确认」终止态
  CODE.COOLING, // 知识测试与重新申请页就地显示冷却到期
  CODE.VALIDATION, // 表单逐字段显示

  // 上传失败就地显示在文件行上（FileUpload 组件）
  CODE.FILE_TOO_LARGE,
  CODE.FILE_TYPE,
  CODE.FILE_CATEGORY,
  CODE.FILE_STORE_DOWN,
  CODE.FILE_REF_INVALID, // BankAccountsView 提交处

  // 资金表单就地显示（DepositView / WithdrawView 的 submitError）
  CODE.FUNDING_DIRECTION_BLOCKED,
  CODE.AMOUNT_INVALID,
  CODE.NO_RATE,
  CODE.VOUCHER_INVALID,
  CODE.RECEIVING_ACCOUNT_INVALID, // DepositView：清所选收款账户 + 重拉列表
  CODE.WITHDRAW_UNKNOWN,
  CODE.FUNDING_DOWN,
]
